/**
 * Renderer wrappers over the transfer channels of `window.electronAPI.azureStorage`
 * (S1: the SDK download backend is the only transfer producer, so the surface
 * lives on the storage preload; the transfers feature itself stays engine-agnostic
 * by touching the preload only here). Handlers never throw; every answer is a
 * `StorageIpcResult<T>` and these helpers hand it back unchanged.
 */
import type { AzureStorageApi } from '../../../types/azureStorageApi';
import type { StorageIpcResult, StorageOperationError } from '../../../types/azureStorageModel';
import type { TransferCollisionDecision, TransferJob } from '../../../types/azureStorageTransfers';

export type TransfersApi = Pick<AzureStorageApi, 'transferCancel' | 'transferResume' | 'transferRestart' | 'transferList' | 'transferClearFinished' | 'transferDecide' | 'onTransferProgress'> & Partial<Pick<AzureStorageApi, 'transferForget'>>;

export const TRANSFERS_API_UNAVAILABLE: StorageOperationError = { category: 'unsupported', code: 'ApiUnavailable', retryable: false, message: 'Transfers are not available in this build.' };

export function getTransfersApi(): TransfersApi | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.electronAPI?.azureStorage;
}

const unavailable = <T,>(): StorageIpcResult<T> => ({ ok: false, error: TRANSFERS_API_UNAVAILABLE });

export async function listTransfers(): Promise<StorageIpcResult<{ jobs: TransferJob[] }>> {
  const api = getTransfersApi();
  return api ? api.transferList({}) : unavailable();
}

export async function cancelTransfer(jobId: string): Promise<StorageIpcResult<Record<string, never>>> {
  const api = getTransfersApi();
  return api ? api.transferCancel({ jobId }) : unavailable();
}

export async function resumeTransfer(jobId: string): Promise<StorageIpcResult<{ attempt: number }>> {
  const api = getTransfersApi();
  return api ? api.transferResume({ jobId }) : unavailable();
}

export async function restartTransfer(jobId: string): Promise<StorageIpcResult<{ attempt: number }>> {
  const api = getTransfersApi();
  return api ? api.transferRestart({ jobId }) : unavailable();
}

export async function retryTransferItems(jobId: string, items: string[]): Promise<StorageIpcResult<{ attempt: number }>> {
  const api = getTransfersApi();
  return api ? api.transferRestart({ jobId, items }) : unavailable();
}

export async function decideTransferCollision(jobId: string, item: string, decision: TransferCollisionDecision['action'], destination?: string): Promise<StorageIpcResult<Record<string, never>>> {
  const api = getTransfersApi();
  return api ? api.transferDecide({ jobId, item, decision, destination }) : unavailable();
}

/** U14: `finished` never touches an unfinished transfer; `unfinished` discards those (after the drawer's confirmation). */
export async function clearFinishedTransfers(scope: 'finished' | 'unfinished'): Promise<StorageIpcResult<{ cleared: string[] }>> {
  const api = getTransfersApi();
  return api ? api.transferClearFinished({ scope }) : unavailable();
}

/** S7 review D F4: `confirm: false` previews where the job's remote leftovers stay; `true` drops the local record. */
export async function forgetTransfer(jobId: string, confirm: boolean): Promise<StorageIpcResult<{ forgotten: boolean; leftBehind: string[] }>> {
  const api = getTransfersApi();
  return api?.transferForget ? api.transferForget({ jobId, confirm }) : unavailable();
}

/** Fire-and-forget analytics (non-React contexts). Properties are enums and buckets only. */
export function trackTransferEvent(eventName: string, props?: Record<string, string | number | boolean>): void {
  if (typeof window === 'undefined') return;
  const track = window.electronAPI?.analytics?.track;
  if (!track) return;
  track(eventName, props).catch(() => { /* analytics never affects UX */ });
}

/** Reveal a finished download in the OS file manager (best-effort; hidden when the helper is absent). */
export function canRevealTransferFile(): boolean {
  return typeof window !== 'undefined' && typeof window.electronAPI?.showItemInFolder === 'function';
}

export function revealTransferFile(path: string): void {
  void window.electronAPI?.showItemInFolder?.(path).catch(() => { /* best-effort */ });
}
